// src/pages/BlogPage.js
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchBlogPostBySlug } from '../services/apiService';
import SEO from '../components/SEO';
import AuthorBio from '../components/AuthorBio';
import RelatedBlogs from '../components/RelatedBlogs';
import { SiX, SiLinkedin, SiFacebook } from 'react-icons/si';

// Social Share Component (no changes needed here)
const SocialShare = ({ url, title }) => {
    const shareText = `Check out this article from Oolook: ${title}`;
    return (
        <div className="flex items-center gap-4">
            <span className="text-sm font-semibold text-dark-text-secondary">Share:</span>
            <a href={`https://twitter.com/intent/tweet?url=${url}&text=${shareText}`} target="_blank" rel="noopener noreferrer" className="text-dark-text-secondary/60 hover:text-white"><SiX /></a>
            <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`} target="_blank" rel="noopener noreferrer" className="text-dark-text-secondary/60 hover:text-white"><SiLinkedin /></a>
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer" className="text-dark-text-secondary/60 hover:text-white"><SiFacebook /></a>
        </div>
    );
};

// ❌ REDUNDANT LOCAL TRANSFORM FUNCTION HAS BEEN REMOVED

const BlogPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const getPostData = async () => {
      setLoading(true);
      setError('');
      try {
        // ✅ CLEANUP: This function now returns a single, fully-transformed post object or null
        const postData = await fetchBlogPostBySlug(slug);
        
        if (postData) {
          // No need to transform here, just set the state directly
          setPost(postData);
        } else {
          setError('Post not found.');
        }
      } catch (err) {
        setError('Failed to load post.');
        console.error(err);
      }
      setLoading(false);
    };
    getPostData();
  }, [slug]);

  if (loading) return <div className="text-center py-48 text-dark-text-primary">Loading post...</div>;
  if (error) return <div className="text-center py-48 text-red-400">{error}</div>;
  if (!post) return null;
  
  const { id, title, content, dateTime, author, featuredMedia, categories } = post;
  const pageUrl = `https://oolook.in/blog/${slug}`;
  const plainTitle = title.replace(/<[^>]*>/g, '');

  return (
    <>
      <SEO
        title={plainTitle}
        description={content.replace(/<[^>]*>/g, '').substring(0, 155)}
        image={featuredMedia?.source_url}
        article={{ title: plainTitle, publishedTime: dateTime, authorName: author?.name, section: categories?.[0]?.name }}
      />
      <main className="bg-dark-bg pt-24 sm:pt-32">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="text-center">
            {categories && categories.length > 0 && (
                <p className="text-base font-semibold text-dark-accent-start">
                    {categories[0].name}
                </p>
            )}
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-dark-text-primary sm:text-5xl" dangerouslySetInnerHTML={{ __html: title }} />
            <div className="mt-6 flex items-center justify-center gap-x-4">
                <div className="flex-shrink-0">
                    <Link to={`/blog/author/${author?.id || 1}`}>
                        <span className="sr-only">{author?.name}</span>
                        {author?.avatarUrl ? (
                            <img className="h-10 w-10 rounded-full" src={author.avatarUrl} alt={author.name} />
                        ) : (
                            <div className="h-10 w-10 rounded-full bg-dark-card flex items-center justify-center text-dark-text-primary font-bold">{author?.name?.charAt(0) || 'O'}</div>
                        )}
                    </Link>
                </div>
                <div>
                    <p className="text-sm font-semibold text-dark-text-primary">By <Link to={`/blog/author/${author?.id || 1}`} className="hover:underline">{author?.name || 'Oolook Team'}</Link></p>
                    <p className="text-xs text-dark-text-secondary/70">Published on {new Date(dateTime).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                </div>
            </div>
          </div>

          {featuredMedia?.source_url && (
              <div className="aspect-video rounded-2xl overflow-hidden my-12">
                  <img src={featuredMedia.source_url} alt={plainTitle} className="w-full h-full object-cover" />
              </div>
          )}
          
          <div 
            className="prose prose-invert lg:prose-xl prose-headings:font-bold prose-a:text-dark-accent-start hover:prose-a:text-dark-accent-end mx-auto"
            dangerouslySetInnerHTML={{ __html: content }} 
          />
          
          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col gap-8">
              <AuthorBio author={author} />
              <SocialShare url={pageUrl} title={plainTitle} />
          </div>
        </div>
      </main>

      <RelatedBlogs currentPostId={id} category={categories?.[0]?.name} />
    </>
  );
};

export default BlogPage;